import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'dateWithTimezone',
  pure: true,
  standalone: false,
})
export class DateWithTimezonePipe implements PipeTransform {
  constructor(private datePipe: DatePipe) {}

  transform(
    value: any,
    format?: string,
    timeZone?: string,
    timeZoneAbbrOverride?: string
  ) {
    if (!value) {
      return value;
    }

    const date = new Date(value);
    const timeZoneAbbr =
      timeZoneAbbrOverride ?? this.getTimeZoneAbbr(date, timeZone);

    if (format === undefined) {
      format = 'MMM d, y h:mm aaa';
    }

    // DatePipe's `timezone` param only understands UTC offsets (e.g. '-0400'),
    // not IANA zone names, so a named zone must be resolved to its DST-aware
    // offset for this specific date before being handed to DatePipe.
    const offset = timeZone ? this.getUtcOffset(date, timeZone) : undefined;
    const formattedDate = this.datePipe.transform(date, format, offset);
    return `${formattedDate} ${timeZoneAbbr}`;
  }

  private getUtcOffset(date: Date, timeZone: string): string {
    const offsetPart = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    const match = offsetPart?.match(/GMT([+-]\d{2}):?(\d{2})?/);
    if (!match) {
      return '+0000';
    }

    const [, hours, minutes = '00'] = match;
    return `${hours}${minutes}`;
  }

  private getTimeZoneAbbr(date: Date, timeZone?: string): string {
    if (typeof Intl === 'undefined' || !('DateTimeFormat' in Intl)) {
      return '';
    }

    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
      ...(timeZone && { timeZone }),
    });
    const timeZones = formatter.formatToParts(date);

    for (let index = 0; index < timeZones.length; index++) {
      if (timeZones[index].type === 'timeZoneName') {
        return timeZones[index].value;
      }
    }

    return '';
  }
}
